import React from 'react';
import { Box, Card, CardContent, Typography, Avatar, Chip, useTheme, alpha } from '@mui/material';
import { SmartToy as BotIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';

// Re-usable CodeBlock component (can be in a shared utils file too)
const CodeBlock = ({node, inline, className, children, ...props}) => {
  const theme = useTheme();
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : 'text';

  return !inline ? (
    <SyntaxHighlighter
      style={vs2015}
      language={language}
      PreTag="div"
      wrapLines={true}
      showLineNumbers={false}
      customStyle={{ borderRadius: '4px', margin: '8px 0', fontSize: '0.85rem' }}
      {...props}
    >
      {String(children).replace(/\n$/, '')}
    </SyntaxHighlighter>
  ) : (
    <code className={className} {...props} style={{
        backgroundColor: alpha(theme.palette.text.primary, 0.1),
        padding: '2px 4px',
        borderRadius: '3px',
        fontSize: '0.85rem'
      }}>
      {children}
    </code>
  );
};


const AssistantMessage = ({ content, model, timestamp }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(date);
  };

  return (
    <Box sx={{ display: 'flex', alignSelf: 'flex-start', maxWidth: '80%', mt: 1, mb: 1 }}>
      <Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 36, height: 36, mb: 0.5 }}>
          <BotIcon fontSize="small" />
        </Avatar>
        {model && (
          <Chip
            label={model.split('/').pop().substring(0,15)}
            size="small"
            sx={{
              height: 20,
              '& .MuiChip-label': { px: 0.8, fontSize: '0.6rem' },
              mb:0.5,
              backgroundColor: alpha(theme.palette.secondary.light, 0.3)
            }}
            title={model}
          />
        )}
        {timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
            {formatDate(timestamp)}
          </Typography>
        )}
      </Box>
       <Box sx={{ flexGrow: 1 }}>
          <Card sx={{
              backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : alpha(theme.palette.grey[50],0.9),
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '4px 16px 16px 16px',
          }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <ReactMarkdown
                components={{
                  code: CodeBlock,
                  p: ({ node, ...props }) => <Typography variant="body1" component="div" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} {...props} />
                }}
              >
                {content}
              </ReactMarkdown>
            </CardContent>
          </Card>
       </Box>
    </Box>
  );
};

export default AssistantMessage;